import { useEffect } from "react";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import PropTypes from "prop-types";
import { loginRequest } from "./authConfig";
import { PageLayout } from "./components/PageLayout";
import { SecurityPage } from "./components/SecurityPage";

import "./styles/App.css";

/**
 * Most applications will need to conditionally render certain components based on whether a user is signed in or not.
 * msal-react provides 2 easy ways to do this. AuthenticatedTemplate and UnauthenticatedTemplate components will
 * only render their children if a user is authenticated or unauthenticated, respectively. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const MainContent = () => {
  /**
   * useMsal is hook that returns the PublicClientApplication instance,
   * that tells you what msal is currently doing. For more, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
   */
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    const login = async () => {
      try {
        const res = await instance.acquireTokenSilent({
          ...loginRequest,
          prompt: "none",
        });
        console.debug(res);
      } catch {
        instance.loginRedirect({
          ...loginRequest,
          prompt: "select_account",
        });
      }
    };

    if (!isAuthenticated && inProgress === "none") login();
  }, [instance, inProgress, isAuthenticated]);
  return <div className="App">{isAuthenticated ? <SecurityPage /> : null}</div>;
};

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const App = ({ instance }) => {
  return (
    <MsalProvider instance={instance}>
      <PageLayout>
        <MainContent />
      </PageLayout>
    </MsalProvider>
  );
};

App.propTypes = {
  instance: PropTypes.object.isRequired,
};

export default App;
